import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_BASE_URL } from '@core/config/api.config';
import { ApiEndpoints } from '@core/config/api-endpoints';
import {
  PaginatedResult,
  PaginationParams,
  createPaginatedResult,
} from '@core/common/models/pagination.models';
import { ProductAttributeValue } from '@features/catalog/models/product-attribute-definition.models';
import { AttributeValueMutationResult, AttributeValueSiblingEnablementResult } from '@features/catalog/models/product-attribute-value.models';
import {
  GraphqlMutationAttributeValuePayloadDto,
} from './dto/product-attribute-value.dto';
import {
  mapMutationValuePayload,
  mapProductAttributeValueDto,
} from './mappers/product-attribute-value.mapper';
import { ensureNoTopLevelErrors, GraphqlHttpResponse, mapEntityMutationResult, mapLifecycleMutationResult } from './mappers/graphql-error.mapper';

const LIST_QUERY = `
  query GetProductAttributeValues($productAttributeDefinitionId: ID!, $activeOnly: Boolean = true, $limit: Int = 50, $offset: Int = 0) {
    productAttributeValues(productAttributeDefinitionId: $productAttributeDefinitionId, activeOnly: $activeOnly, limit: $limit, offset: $offset) {
      items {
        id
        companyId
        productAttributeDefinitionId
        value
        position
        active
        productAttributeDefinition {
          id
          name
          slug
        }
      }
      total
      limit
      offset
    }
  }
`;

const ENABLE_ON_PRODUCTS_MUTATION = `
  mutation EnableProductAttributeValueOnProducts(
    $productAttributeDefinitionId: ID!
    $value: String!
    $productIds: [ID!]
    $excludeProductId: ID
  ) {
    enableProductAttributeValueOnProducts(
      productAttributeDefinitionId: $productAttributeDefinitionId
      value: $value
      productIds: $productIds
      excludeProductId: $excludeProductId
    ) {
      attributeValue {
        id
        productAttributeDefinitionId
        value
        position
        active
      }
      enabledProductIds
      errors
    }
  }
`;

const CREATE_MUTATION = `
  mutation CreateProductAttributeValue($productAttributeDefinitionId: ID!, $value: String!, $position: Int, $active: Boolean) {
    createProductAttributeValue(productAttributeDefinitionId: $productAttributeDefinitionId, value: $value, position: $position, active: $active) {
      attributeValue {
        id
        productAttributeDefinitionId
        value
        position
        active
      }
      errors
    }
  }
`;

const UPDATE_MUTATION = `
  mutation UpdateProductAttributeValue($id: ID!, $value: String, $position: Int, $active: Boolean) {
    updateProductAttributeValue(id: $id, value: $value, position: $position, active: $active) {
      attributeValue {
        id
        value
        position
        active
      }
      errors
    }
  }
`;

const DESTROY_MUTATION = `
  mutation DestroyProductAttributeValue($id: ID!) {
    destroyProductAttributeValue(id: $id) {
      attributeValue {
        id
        value
        active
      }
      errors
    }
  }
`;

const PERMANENTLY_DELETE_MUTATION = `
  mutation PermanentlyDeleteProductAttributeValue($id: ID!) {
    permanentlyDeleteProductAttributeValue(id: $id) {
      success
      errors
    }
  }
`;

@Injectable()
export class AttributeValueApiService {
  constructor(
    private readonly http: HttpClient,
    @Inject(API_BASE_URL) private readonly baseUrl: string
  ) {}

  private graphqlUrl(): string {
    return `${this.baseUrl}${ApiEndpoints.graphql}`;
  }

  list(
    productAttributeDefinitionId: string,
    activeOnly: boolean | null = true,
    pagination?: PaginationParams
  ): Observable<PaginatedResult<ProductAttributeValue>> {
    const limit = pagination?.limit ?? 50;
    const offset = pagination?.offset ?? 0;
    return this.http
      .post<GraphqlHttpResponse<{ productAttributeValues: { items: any[]; total: number; limit: number; offset: number } }>>(
        this.graphqlUrl(),
        {
          query: LIST_QUERY,
          variables: { productAttributeDefinitionId, activeOnly, limit, offset },
        }
      )
      .pipe(
        map((res) => ensureNoTopLevelErrors(res)),
        map((data) => {
          const values = (data.productAttributeValues?.items ?? []).map(mapProductAttributeValueDto);
          return createPaginatedResult({
            items: values,
            total: data.productAttributeValues?.total ?? 0,
            limit: data.productAttributeValues?.limit ?? limit,
            offset: data.productAttributeValues?.offset ?? offset,
          });
        })
      );
  }

  create(input: {
    productAttributeDefinitionId: string;
    value: string;
    position?: number | null;
    active?: boolean;
  }): Observable<AttributeValueMutationResult> {
    const variables: Record<string, unknown> = {
      productAttributeDefinitionId: input.productAttributeDefinitionId,
      value: input.value.trim(),
      position: input.position ?? null,
      active: input.active ?? true,
    };
    return this.http
      .post<
        GraphqlHttpResponse<{
          createProductAttributeValue?: GraphqlMutationAttributeValuePayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: CREATE_MUTATION,
        variables,
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res)),
        map((data) => mapMutationValuePayload(data.createProductAttributeValue))
      );
  }

  enableOnProducts(input: {
    productAttributeDefinitionId: string;
    value: string;
    productIds?: string[] | null;
    excludeProductId?: string | null;
  }): Observable<AttributeValueSiblingEnablementResult> {
    const variables: Record<string, unknown> = {
      productAttributeDefinitionId: input.productAttributeDefinitionId,
      value: input.value.trim(),
      productIds: input.productIds?.length ? input.productIds : null,
      excludeProductId: input.excludeProductId?.trim() || null,
    };
    return this.http
      .post<
        GraphqlHttpResponse<{
          enableProductAttributeValueOnProducts?: {
            attributeValue?: GraphqlMutationAttributeValuePayloadDto['attributeValue'];
            enabledProductIds?: Array<string | number> | null;
            errors?: unknown;
          } | null;
        }>
      >(this.graphqlUrl(), {
        query: ENABLE_ON_PRODUCTS_MUTATION,
        variables,
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res)),
        map((data) => {
          const payload = data.enableProductAttributeValueOnProducts;
          const mapped = mapMutationValuePayload(
            payload
              ? { attributeValue: payload.attributeValue, errors: payload.errors }
              : null
          );
          return {
            value: mapped.value,
            enabledProductIds: (payload?.enabledProductIds ?? []).map((id) => String(id)),
            errors: mapped.errors,
          };
        })
      );
  }

  update(input: {
    id: string;
    value?: string;
    position?: number | null;
    active?: boolean;
  }): Observable<AttributeValueMutationResult> {
    const variables: Record<string, unknown> = { id: input.id };
    if (input.value !== undefined) {
      variables['value'] = input.value.trim();
    }
    if (input.position !== undefined) {
      variables['position'] = input.position;
    }
    if (input.active !== undefined) {
      variables['active'] = input.active;
    }
    return this.http
      .post<
        GraphqlHttpResponse<{
          updateProductAttributeValue?: GraphqlMutationAttributeValuePayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: UPDATE_MUTATION,
        variables,
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res)),
        map((data) => mapMutationValuePayload(data.updateProductAttributeValue))
      );
  }

  destroy(id: string): Observable<AttributeValueMutationResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          destroyProductAttributeValue?: GraphqlMutationAttributeValuePayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: DESTROY_MUTATION,
        variables: { id },
      })
      .pipe(
        map((res) =>
          mapEntityMutationResult(res, 'destroyProductAttributeValue', (payload) =>
            mapMutationValuePayload(payload as GraphqlMutationAttributeValuePayloadDto | null | undefined)
          )
        )
      );
  }

  updateActive(id: string, active: boolean): Observable<AttributeValueMutationResult> {
    return this.update({ id, active });
  }

  permanentlyDelete(id: string): Observable<{ success: boolean; errors: string[] }> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          permanentlyDeleteProductAttributeValue?: { success?: boolean; errors?: string[] } | null;
        }>
      >(this.graphqlUrl(), {
        query: PERMANENTLY_DELETE_MUTATION,
        variables: { id },
      })
      .pipe(map((res) => mapLifecycleMutationResult(res, 'permanentlyDeleteProductAttributeValue')));
  }
}
